import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import {
  useAppData,
  type CreateEntityViewInput,
  type EntityViewRow,
} from "#app/lib/data";
import { AnalyticsContext, ViewEvent, useAnalytics } from "#app/lib/analytics";
import { canEdit } from "#app/lib/utils/access-level";
import {
  useEntityViews,
  type EntityViewsScope,
} from "#app/lib/hooks/use-entity-views";
import { findViewByKey } from "#app/lib/views/view-routes";
import { usePermission } from "#app/lib/hooks/use-permission";
import {
  DEFAULT_VIEW_CONFIG,
  parseViewConfig,
  viewConfigDefaults,
  viewConfigDirtyKeys,
  type ViewConfig,
} from "#app/lib/view-config";

/**
 * The saved-view scope for one entity area (a workflow's tab row).
 *
 * TWO layers, deliberately:
 *   saved   the `config` on the server row — what every member of the
 *           workspace sees when they open the view
 *   draft   the in-session edit, which the views render from immediately so
 *           a toggle previews before it is committed
 *
 * The panel's Save button counts `dirtyKeys` between them. Nothing writes on
 * a toggle: a shared view changes for everyone, so committing is explicit.
 *
 * `config.filters` and `config.advancedFilters` are edited through
 * `IssueFilterProvider`'s VIEW layer, which seeds from the draft, reports
 * every edit through `setConfig` like any other panel control, and re-seeds
 * on `configSeed`. The Customize view Filters page (`ViewFilters`) is that
 * layer's only editor; the toolbar filter panel edits a temporary session
 * pool that never touches the draft.
 */

export type ViewPanelPage = "main" | "fields" | "filters" | "more" | "workflows";

/** Asked before the open panel changes page: true lets the change run now;
 *  false keeps it, and the guard runs `proceed` later if it still applies. */
export type PanelLeaveGuard = (proceed: () => void) => boolean;

type ViewsContextValue = {
  scope: EntityViewsScope;
  views: EntityViewRow[];
  initialLoading: boolean;
  loadError: Error | null;
  retry: () => void;
  activeView: EntityViewRow | null;
  /**
   * The route names a view the LOADED row does not hold — a deleted view's
   * URL outliving it in a bookmark, another tab, `preferences.defaultRoute`.
   * Only a non-empty row can say so: an empty row is a failed load or an
   * unseeded scope, and a route that read it as "deleted" would bounce to the
   * workflow index, whose empty-row floor bounces straight back — the two
   * redirects loop until the browser throttles the tab.
   */
  activeViewGone: boolean;
  /** The committed config of the active view. */
  savedConfig: ViewConfig;
  /** The active view body's defaults (`viewConfigDefaults`) — what "Reset
   *  view to defaults" restores. */
  defaultConfig: ViewConfig;
  /** The config the views render from — saved plus uncommitted edits. */
  config: ViewConfig;
  setConfig: (patch: Partial<ViewConfig>) => void;
  dirtyKeys: Array<keyof ViewConfig>;
  /** Bumped whenever the draft is retired — a view switch, a save, a
   *  discard, a remote edit landing. An editor that seeds itself from the
   *  config once and then owns its own state (the filter editors) keys on
   *  this so it re-seeds exactly when the config underneath it changed for a
   *  reason other than its own edits. */
  configSeed: number;
  /** The one gate on every write in this scope: `create.views` on a
   *  workflow's views, the viewer's own level on a workspace dashboard (its
   *  creator and edit-level shares write it; the role grants nothing). */
  canManage: boolean;
  /** The scope's PINNED views in position order — the rows' `isPinned`. A
   *  pin is a property of the shared view, like its name and its default:
   *  the strip hoists these to the front for every member and holds them
   *  there. The per-viewer layer is the drag ORDER only. */
  pinnedIds: string[];
  /** Pins or unpins the view for the workspace — `create.views`. Resolves
   *  true once the row is written. */
  setPinned: (id: string, pinned: boolean) => Promise<boolean>;
  saving: boolean;
  /** `via` names the control for `view.save`: "button" or "hotkey". */
  save: (via?: string) => Promise<void>;
  discard: () => void;
  /** The Discard control: `discard` plus `view.discard_changes`. A save as a
   *  new tab retires the draft with the bare `discard`. */
  discardChanges: () => void;
  panelOpen: boolean;
  panelPage: ViewPanelPage;
  /** `via` names the control for `view.open_panel`. */
  openPanel: (page?: ViewPanelPage, via?: string) => void;
  /** Arms the panel to open when `viewId` becomes the active view — for
   *  "Customize view" on a NON-active tab, which navigates first. The pending
   *  open lives here, not in the tab: the workflow shell re-keys its filter
   *  scope per view, so the navigation remounts the tab strip and any state a
   *  tab held dies before the view becomes active. */
  requestPanelForView: (viewId: string) => void;
  closePanel: () => void;
  setPanelPage: (page: ViewPanelPage) => void;
  /** The panel's shell registers here while it holds a typed draft. A page
   *  change on the OPEN panel asks it first: the guard answers false to keep
   *  the change, and runs `proceed` itself once the draft is discarded. */
  setPanelLeaveGuard: (guard: PanelLeaveGuard | null) => void;
  /** `via` names the control for `view.create`: "add_menu",
   *  "tab_duplicate" or "save_as_new_tab". */
  createView: (
    input: CreateEntityViewInput,
    via?: string,
  ) => Promise<EntityViewRow | null>;
  updateView: ReturnType<typeof useEntityViews>["updateView"];
  /** Sends one `view.*` event with the view's type, scope and built-in
   *  flag, for the controls that write through `updateView` or no write. */
  trackView: (
    event: (typeof ViewEvent)[keyof typeof ViewEvent],
    view: EntityViewRow | undefined,
    data?: Record<string, unknown>,
  ) => void;
  deleteView: ReturnType<typeof useEntityViews>["deleteView"];
  setDefaultView: ReturnType<typeof useEntityViews>["setDefaultView"];
};

const ViewsContext = createContext<ViewsContextValue | null>(null);

type ViewsProviderProps = {
  scope: EntityViewsScope;
  /** The route's active view: a view id, or a built-in key (`list`). */
  activeKey: string | null;
  children: React.ReactNode;
};

export function ViewsProvider({
  scope,
  activeKey,
  children,
}: ViewsProviderProps) {
  const {
    views,
    initialLoading,
    loadError,
    retry,
    createView: createEntityView,
    updateView,
    deleteView: deleteEntityView,
    setDefaultView: setEntityDefaultView,
  } = useEntityViews(scope);
  const hasCreateViews = usePermission("create.views");
  const { currentUser } = useAppData();
  const { track } = useAnalytics();

  // Each view event carries what the view is, never which one it is.
  const viewsRef = React.useRef(views);
  viewsRef.current = views;
  const trackView = useCallback(
    (
      event: (typeof ViewEvent)[keyof typeof ViewEvent],
      view: EntityViewRow | undefined,
      data: Record<string, unknown> = {},
    ) => {
      track({
        context: AnalyticsContext.View,
        event,
        data: {
          type: view?.type ?? "unknown",
          scope: scope.kind,
          builtIn: view?.builtInKey != null,
          ...data,
        },
      });
    },
    [scope.kind, track],
  );
  const findView = useCallback(
    (id: string) => viewsRef.current.find((view) => view.id === id),
    [],
  );

  const createView = useCallback(
    async (input: CreateEntityViewInput, via = "unknown") => {
      const created = await createEntityView(input);
      if (created) trackView(ViewEvent.Create, created, { via });
      return created;
    },
    [createEntityView, trackView],
  );
  const deleteView = useCallback(
    async (id: string) => {
      const view = findView(id);
      const ok = await deleteEntityView(id);
      if (ok) trackView(ViewEvent.Delete, view);
      return ok;
    },
    [deleteEntityView, findView, trackView],
  );
  const setDefaultView = useCallback(
    async (id: string, isDefault: boolean) => {
      const ok = await setEntityDefaultView(id, isDefault);
      if (ok) trackView(ViewEvent.SetDefault, findView(id), { isDefault });
      return ok;
    },
    [findView, setEntityDefaultView, trackView],
  );

  // `views` arrives in the server's position order, so the pinned run is
  // already in strip order.
  const pinnedIds = useMemo(
    () => views.filter((view) => view.isPinned).map((view) => view.id),
    [views],
  );
  const setPinned = useCallback(
    async (id: string, pinned: boolean) => {
      const saved = await updateView(id, { isPinned: pinned });
      if (saved === null) return false;
      trackView(pinned ? ViewEvent.Pin : ViewEvent.Unpin, saved);
      return true;
    },
    [trackView, updateView],
  );

  const activeView = useMemo(
    () => findViewByKey(views, activeKey),
    [views, activeKey],
  );

  // A workspace dashboard's row carries its own ACL; the server resolves the
  // creator to full_edit, and the compare here keeps a stale cached level
  // from locking an owner out of their own panel.
  const canManage =
    scope.kind === "workspace"
      ? (activeView?.createdBy != null &&
          activeView.createdBy === currentUser?.userId) ||
        canEdit(activeView?.viewerAccessLevel)
      : hasCreateViews;

  // The body's defaults fill what the stored config leaves out and are what
  // Reset restores — a list and a board start their empty stages differently.
  const defaultConfig = useMemo(
    () =>
      activeView ? viewConfigDefaults(activeView.type, scope.kind) : DEFAULT_VIEW_CONFIG,
    [activeView, scope.kind],
  );
  const savedConfig = useMemo(
    () =>
      activeView
        ? parseViewConfig(activeView.config, defaultConfig)
        : defaultConfig,
    [activeView, defaultConfig],
  );

  const [draft, setDraft] = useState<ViewConfig | null>(null);
  const [configSeed, setConfigSeed] = useState(0);
  // The draft belongs to ONE saved config. Switching views, and a save or a
  // remote edit landing, both retire it — a draft carried across would apply
  // one view's edits to another's baseline.
  const baseline = `${activeView?.id ?? ""}|${JSON.stringify(savedConfig)}`;
  const baselineRef = React.useRef(baseline);
  if (baselineRef.current !== baseline) {
    baselineRef.current = baseline;
    setDraft(null);
    setConfigSeed((seed) => seed + 1);
  }

  const config = draft ?? savedConfig;

  const savedConfigRef = React.useRef(savedConfig);
  savedConfigRef.current = savedConfig;

  // Reads the baseline off a ref so the callback identity holds across a
  // config change — the Filters page's report effect depends on it, and a
  // new identity per keystroke would re-fire the sync it just caused.
  const setConfig = useCallback((patch: Partial<ViewConfig>) => {
    setDraft((prev) => ({ ...(prev ?? savedConfigRef.current), ...patch }));
  }, []);

  const dirtyKeys = useMemo(
    () => (draft ? viewConfigDirtyKeys(savedConfig, draft) : []),
    [draft, savedConfig],
  );

  const [saving, setSaving] = useState(false);

  const save = useCallback(async (via = "button") => {
    if (!activeView || !draft) return;
    const keys = viewConfigDirtyKeys(savedConfig, draft);
    setSaving(true);
    try {
      // Spread over the RAW stored config: a key this build doesn't know
      // belongs to a newer panel, and a save here must not drop it.
      const raw =
        activeView.config && typeof activeView.config === "object"
          ? (activeView.config as Record<string, unknown>)
          : {};
      const saved = await updateView(activeView.id, {
        config: { ...raw, ...draft },
      });
      if (saved) {
        setDraft(null);
        trackView(ViewEvent.Save, activeView, { keys, via });
      }
    } finally {
      setSaving(false);
    }
  }, [activeView, draft, savedConfig, trackView, updateView]);

  // Discarding leaves `savedConfig` untouched, so the baseline guard above
  // never sees it — the seed is bumped here explicitly.
  const discard = useCallback(() => {
    setDraft(null);
    setConfigSeed((seed) => seed + 1);
  }, []);
  const discardChanges = useCallback(() => {
    trackView(ViewEvent.DiscardChanges, activeView ?? undefined, {
      keys: dirtyKeys,
    });
    discard();
  }, [activeView, dirtyKeys, discard, trackView]);

  const [panelOpen, setPanelOpen] = useState(false);
  const [panelPage, setPanelPage] = useState<ViewPanelPage>("main");

  // The panel always opens on the page it was asked for, so a close/reopen
  // never lands on a sub-page the person already backed out of.
  const activeViewRef = React.useRef(activeView);
  activeViewRef.current = activeView;
  // Every caller (the toolbar, a filter chip, a tab's "Customize view")
  // runs through here, so a page change on the open panel is guarded once
  // for all of them.
  const leaveGuardRef = React.useRef<PanelLeaveGuard | null>(null);
  const setPanelLeaveGuard = useCallback((guard: PanelLeaveGuard | null) => {
    leaveGuardRef.current = guard;
  }, []);
  const panelRef = React.useRef({ panelOpen, panelPage });
  panelRef.current = { panelOpen, panelPage };
  const openPanel = useCallback(
    (page: ViewPanelPage = "main", via = "unknown") => {
      const proceed = () => {
        setPanelPage(page);
        setPanelOpen(true);
        trackView(ViewEvent.OpenPanel, activeViewRef.current ?? undefined, {
          page,
          via,
        });
      };
      const { panelOpen: isOpen, panelPage: currentPage } = panelRef.current;
      const guard = leaveGuardRef.current;
      if (isOpen && page !== currentPage && guard && !guard(proceed)) return;
      proceed();
    },
    [trackView],
  );
  const closePanel = useCallback(() => setPanelOpen(false), []);

  const [pendingPanelViewId, setPendingPanelViewId] = useState<string | null>(
    null,
  );
  const requestPanelForView = useCallback((viewId: string) => {
    setPendingPanelViewId(viewId);
  }, []);
  const activeViewId = activeView?.id ?? null;
  const prevActiveViewIdRef = React.useRef(activeViewId);
  React.useEffect(() => {
    const prevActiveViewId = prevActiveViewIdRef.current;
    prevActiveViewIdRef.current = activeViewId;
    if (!pendingPanelViewId) return;
    if (activeViewId === pendingPanelViewId) {
      setPendingPanelViewId(null);
      openPanel("main", "tab_menu");
    } else if (activeViewId !== prevActiveViewId) {
      // Navigation landed on some other view — a pending open left armed
      // would pop the panel on a later, unrelated visit.
      setPendingPanelViewId(null);
    }
  }, [activeViewId, pendingPanelViewId, openPanel]);

  const value = useMemo<ViewsContextValue>(
    () => ({
      scope,
      views,
      initialLoading,
      loadError,
      retry,
      activeView,
      activeViewGone: !initialLoading && views.length > 0 && !activeView,
      savedConfig,
      defaultConfig,
      config,
      setConfig,
      dirtyKeys,
      configSeed,
      canManage,
      pinnedIds,
      setPinned,
      saving,
      save,
      discard,
      discardChanges,
      panelOpen,
      panelPage,
      openPanel,
      requestPanelForView,
      closePanel,
      setPanelPage,
      setPanelLeaveGuard,
      createView,
      updateView,
      trackView,
      deleteView,
      setDefaultView,
    }),
    [
      scope,
      views,
      initialLoading,
      loadError,
      retry,
      activeView,
      savedConfig,
      defaultConfig,
      config,
      setConfig,
      dirtyKeys,
      configSeed,
      canManage,
      pinnedIds,
      setPinned,
      saving,
      save,
      discard,
      discardChanges,
      panelOpen,
      panelPage,
      openPanel,
      requestPanelForView,
      closePanel,
      setPanelLeaveGuard,
      createView,
      updateView,
      trackView,
      deleteView,
      setDefaultView,
    ],
  );

  return <ViewsContext.Provider value={value}>{children}</ViewsContext.Provider>;
}

export function useViews(): ViewsContextValue {
  const ctx = useContext(ViewsContext);
  if (!ctx) throw new Error("useViews must be used within a ViewsProvider");
  return ctx;
}

/**
 * Provider-optional read for the components that render both inside and
 * outside a saved-view scope — `IssuesTable` and `IssuesBoard` also serve the
 * team and member screens, which have no view row. Null means "no saved
 * view": those hosts keep their own local state.
 */
export function useViewsOptional(): ViewsContextValue | null {
  return useContext(ViewsContext);
}

/**
 * The config a view body renders from, plus its writer.
 *
 * Inside a saved-view scope both come from the provider, so a toggle drafts
 * and the panel's Save commits it. Outside one — the team and member issue
 * screens have no view row — they are session state: the body's defaults
 * (`viewType` names the body) under `initial`. One hook so the same controls
 * work on every screen and no body carries two code paths for "is this a
 * saved view".
 */
export function useViewConfigState(
  viewType: "list" | "board",
  initial?: Partial<ViewConfig>,
) {
  const ctx = useContext(ViewsContext);
  // Mount-only seed: `initial` is a scope default, not a live value, and a
  // fresh object per render would reset the local config on every keystroke.
  const [local, setLocal] = useState<ViewConfig>(() => ({
    ...viewConfigDefaults(viewType),
    ...initial,
  }));
  const setLocalConfig = useCallback((patch: Partial<ViewConfig>) => {
    setLocal((prev) => ({ ...prev, ...patch }));
  }, []);
  // Only an ACTIVE view owns the config. A scope's non-view tabs (a team's
  // Board, Overview) sit inside the provider with no view, and would
  // otherwise render from the bare default rather than their own seed.
  const inView = Boolean(ctx?.activeView);
  return {
    config: inView && ctx ? ctx.config : local,
    setConfig: inView && ctx ? ctx.setConfig : setLocalConfig,
    /** True inside a saved view — the edits above settle on Save. */
    saved: inView,
  };
}

/**
 * `view.change_option` for a Display, More options or field switch. `key`
 * names the config key; a custom field sends its kind, never its id. Outside
 * a saved-view scope the call does nothing.
 */
export function useTrackViewOption() {
  const ctx = useContext(ViewsContext);
  const trackView = ctx?.trackView;
  const activeView = ctx?.activeView ?? undefined;
  return useCallback(
    (key: string, data: Record<string, unknown> = {}) => {
      trackView?.(ViewEvent.ChangeOption, activeView, { key, ...data });
    },
    [activeView, trackView],
  );
}
